import type { StringFilterColumnCondition } from "@/nzds/data-grid/string-filter-column-url"
import {
  readStringFilterColumn,
  writeStringFilterColumn,
} from "@/nzds/data-grid/string-filter-column-url"
import {
  gridUrlValuesKey,
  withGridUrlUpdates,
} from "@/nzds/data-grid/use-grid-url-state"

import { type GridFilterModel, GridLogicOperator } from "@mui/x-data-grid-pro"

export const ADMIN_SITES_GRID_URL_KEYS = [
  "address",
  "address_op",
  "logic",
  "name",
  "name_op",
] as const

type AdminSitesFilterLogic = "and" | "or"

interface AdminSitesGridUrlState {
  address: StringFilterColumnCondition | null
  logic: AdminSitesFilterLogic
  name: StringFilterColumnCondition | null
}

interface AdminSitesGridUrlWrite {
  address?: { operator: string; value?: string } | null
  logic?: AdminSitesFilterLogic
  name?: { operator: string; value?: string } | null
}

interface FilterCriterion {
  field: string
  operator: string
  value?: string
}

const NAME_FILTER_ITEM_ID = "admin-sites-name"
const ADDRESS_FILTER_ITEM_ID = "admin-sites-address"

const readLogic = (
  logicRaw: string | null | undefined
): AdminSitesFilterLogic => (logicRaw === "or" ? "or" : "and")

const hasRawColumn = (
  operatorRaw: string | null | undefined,
  valueRaw: string | null | undefined
): boolean =>
  (operatorRaw != null && operatorRaw !== "") ||
  (valueRaw != null && valueRaw !== "")

const getConditionForField = (
  criteria: readonly FilterCriterion[],
  field: string
): { operator: string; value?: string } | null => {
  const match = criteria.find((item) => item.field === field)
  if (!match) {
    return null
  }

  if (match.value === undefined) {
    return { operator: match.operator }
  }

  return { operator: match.operator, value: match.value }
}

const toFilterItem = (
  field: "address" | "name",
  id: string,
  condition: StringFilterColumnCondition
) => ({
  field,
  id,
  operator: condition.operator,
  ...(condition.value !== undefined ? { value: condition.value } : {}),
})

export const readAdminSitesGridUrl = (
  values: Record<string, string | null>
): AdminSitesGridUrlState => ({
  address: readStringFilterColumn(values.address_op, values.address),
  logic: readLogic(values.logic),
  name: readStringFilterColumn(values.name_op, values.name),
})

const writeAdminSitesGridUrl = (
  input: AdminSitesGridUrlWrite
): Record<string, string | null> => {
  const name = writeStringFilterColumn(input.name)
  const address = writeStringFilterColumn(input.address)
  const bothApplied = name.operator != null && address.operator != null

  return {
    address: address.value,
    address_op: address.operator,
    logic: bothApplied && input.logic ? input.logic : null,
    name: name.value,
    name_op: name.operator,
  }
}

/**
 * Keys to delete so the address matches what the panel can show. A recognized
 * column is left alone, and a missing or unrecognized `logic` is not written.
 */
const sanitizeAdminSitesGridUrl = (
  values: Record<string, string | null>
): Record<string, string | null> => {
  const { address, name } = readAdminSitesGridUrl(values)
  const updates: Record<string, string | null> = {}

  if (hasRawColumn(values.name_op, values.name) && !name) {
    updates.name = null
    updates.name_op = null
  }

  if (hasRawColumn(values.address_op, values.address) && !address) {
    updates.address = null
    updates.address_op = null
  }

  const nothingShown = !name && !address
  const clearedAColumn = updates.name_op === null || updates.address_op === null
  if (
    nothingShown &&
    clearedAColumn &&
    values.logic != null &&
    values.logic !== ""
  ) {
    updates.logic = null
  }

  return updates
}

export const toAdminSitesFilterModel = (
  state: AdminSitesGridUrlState
): GridFilterModel => ({
  items: [
    ...(state.name
      ? [toFilterItem("name", NAME_FILTER_ITEM_ID, state.name)]
      : []),
    ...(state.address
      ? [toFilterItem("address", ADDRESS_FILTER_ITEM_ID, state.address)]
      : []),
  ],
  logicOperator:
    state.logic === "or" ? GridLogicOperator.Or : GridLogicOperator.And,
})

export const getAppliedAdminSitesFilters = (
  criteria: readonly FilterCriterion[],
  logicOperator: string
): AdminSitesGridUrlWrite => ({
  address: getConditionForField(criteria, "address"),
  logic: readLogic(logicOperator),
  name: getConditionForField(criteria, "name"),
})

export const getNextAdminSitesGridUrlUpdate = ({
  applied,
  pageParam,
  restoredPage,
  signatureChanged,
  values,
}: {
  applied: AdminSitesGridUrlWrite
  pageParam: string | null
  restoredPage: string
  signatureChanged: boolean
  values: Record<string, string | null>
}): Record<string, string | null> | null => {
  if (!signatureChanged) {
    const corrections = sanitizeAdminSitesGridUrl(values)
    if (Object.keys(corrections).length === 0) {
      return null
    }

    if (pageParam) {
      return corrections
    }

    return { ...corrections, page: restoredPage }
  }

  const updates = writeAdminSitesGridUrl(applied)
  const nextValues = withGridUrlUpdates(values, updates)
  if (
    gridUrlValuesKey(ADMIN_SITES_GRID_URL_KEYS, nextValues) ===
    gridUrlValuesKey(ADMIN_SITES_GRID_URL_KEYS, values)
  ) {
    return null
  }

  return { ...updates, page: "1" }
}
